import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import {
  Phone, Mail, MapPin, ArrowRight, Menu, X,
  Paintbrush, LayoutGrid, Star, Shield,
  Users, ChevronUp, Palette, Hammer, Home, Clock
} from "lucide-react";
import ServicePage from "@/components/ServicePage";
import ContactPage from "@/components/ContactPage";
import LegalPage from "@/components/LegalPage";

/* ═══════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════ */
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1688372199140-cade7ae820fe?w=1200&q=80&fm=webp",
  service1: "/team-malerhandwerk.webp",
  service2: "/boden-card.webp",
  service3: "https://images.unsplash.com/photo-1749704647804-81338ade5546?w=600&q=80&fm=webp",
  service4: "https://images.unsplash.com/photo-1772567732985-6fefa2dff56d?w=600&q=80&fm=webp",
  about: "https://images.unsplash.com/photo-1622250544703-997d69d856e3?w=800&q=80&fm=webp",
  gallery1: "https://images.unsplash.com/photo-1760072513367-55182245e76c?w=600&q=80&fm=webp",
  gallery2: "https://images.unsplash.com/photo-1616697412153-7ad8ac8aa5d9?w=600&q=80&fm=webp",
};

const NAV_LINKS = [
  { label: "Leistungen", href: "/#leistungen" },
  { label: "Über Uns", href: "/#ueber-uns" },
  { label: "Vorteile", href: "/#vorteile" },
  { label: "Kontakt", href: "/kontakt" },
];

const SERVICES = [
  {
    title: "Malerhandwerk",
    slug: "malerhandwerk",
    description: "Innenanstriche, Fassadengestaltung, Lackierarbeiten – wir bringen professionelle Farbe in Ihr Leben.",
    image: IMAGES.service1,
    icon: Paintbrush,
  },
  {
    title: "Bodenhandwerk",
    slug: "bodenhandwerk",
    description: "Teppich, Laminat, Parkett, Vinyl – fachmännisch verlegt für jeden Anspruch und jedes Budget.",
    image: IMAGES.service2,
    icon: LayoutGrid,
  },
  {
    title: "Spezial-Angebote",
    slug: "spezial-angebote",
    description: "Urlaubs-Service, Senioren-Service und Komplettsanierungen – alles aus einer Hand.",
    image: IMAGES.service3,
    icon: Star,
  },
  {
    title: "Referenzen",
    slug: "referenzen",
    description: "Überzeugen Sie sich selbst von unserer Arbeit – privat und gewerblich.",
    image: IMAGES.service4,
    icon: Home,
  },
];

const FEATURES = [
  {
    icon: Hammer,
    title: "Komplettservice",
    slug: "komplettservice",
    description: "Ein Ansprechpartner für alle Gewerke. Wir koordinieren Sanierung, Renovierung und Neubau komplett für Sie.",
  },
  {
    icon: Clock,
    title: "Urlaubs-Service",
    slug: "urlaubs-service",
    description: "Fahren Sie entspannt in den Urlaub – wir renovieren derweil Ihr Zuhause. Zurückkommen und staunen!",
  },
  {
    icon: Users,
    title: "Senioren-Service",
    slug: "senioren-service",
    description: "Einfühlsame und vertrauenswürdige Betreuung für ältere Kunden. Kundennah und zuverlässig.",
  },
  {
    icon: Shield,
    title: "Höchste Qualität",
    slug: "hoechste-qualitaet",
    description: "Modernste Materialien und Techniken für langlebige Ergebnisse. Stets auf aktuellem Stand.",
  },
];

/* ═══════════════════════════════════════════════
   HOOKS
   ═══════════════════════════════════════════════ */
function useScrollPosition() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
}

function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          obs.unobserve(entry.target);
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isRevealed };
}

function useCounter(target, duration = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!started) return;
    let current = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      current += step;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.ceil(current));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [started, target, duration]);
  return { count, ref };
}

/* ═══════════════════════════════════════════════
   SMALL COMPONENTS
   ═══════════════════════════════════════════════ */
function RevealOnScroll({ children, className = "", delay = 0 }) {
  const { ref, isRevealed } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isRevealed ? 1 : 0,
        transform: isRevealed ? "translateY(0)" : "translateY(30px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

function LogoMark({ size = 40 }) {
  return (
    <img
      src="/logo-reinert.webp"
      alt="Malerbetrieb Reinert – Malerarbeiten und Bodenbeläge in Bad Neuenahr-Ahrweiler"
      style={{ height: size, width: 'auto' }}
      data-testid="logo-mark"
    />
  );
}

function BrushStrokeUnderline() {
  return (
    <div style={{ width: "100%", maxWidth: "100%" }}>
      <svg viewBox="0 0 300 12" preserveAspectRatio="none" className="brush-underline" style={{ width: "100%", maxWidth: "100%" }}>
        <line
          x1="0" y1="6" x2="300" y2="6"
          stroke="#FFCC00" strokeWidth="5" strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

function WaveDivider({ flip = false }) {
  const { ref, isRevealed } = useScrollReveal(0.3);
  return (
    <div
      ref={ref}
      className={`brush-stroke-divider ${flip ? "flip" : ""} ${isRevealed ? "revealed" : ""}`}
    >
      <img
        src="/brush-stroke.webp"
        alt=""
        aria-hidden="true"
        draggable="false"
      />
    </div>
  );
}

function StatItem({ target, suffix = "", label }) {
  const { count, ref } = useCounter(target);
  return (
    <div ref={ref} className="text-center px-4 py-2" data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="text-3xl md:text-4xl lg:text-5xl font-display font-bold" style={{ color: '#FFCC00', textShadow: '0 2px 12px rgba(0,0,50,0.7), 0 0 4px rgba(0,0,80,0.4)' }}>
        {count}{suffix}
      </div>
      <div className="text-white text-sm md:text-base mt-1 font-body tracking-wide uppercase font-semibold" style={{ textShadow: '0 1px 8px rgba(0,0,50,0.8), 0 0 3px rgba(0,0,80,0.5)' }}>
        {label}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PAINT LOAD ANIMATION
   ═══════════════════════════════════════════════ */
function PaintLoadAnimation() {
  const [done, setDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDone(true), 1600);
    return () => clearTimeout(t);
  }, []);
  if (done) return null;
  return (
    <div className="paint-load-overlay" data-testid="paint-load-animation">
      <div className="paint-load-stripe" />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   NAVIGATION
   ═══════════════════════════════════════════════ */
function Navigation() {
  const scrolled = useScrollPosition();
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: pos, behavior: "smooth" });
    }
  };

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <nav
        className={`nav-bar fixed top-0 left-0 right-0 z-50 bg-white ${scrolled ? "scrolled" : ""}`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3 bg-transparent border-none cursor-pointer"
            data-testid="logo-button"
          >
            <LogoMark size={72} />
          </button>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link font-body no-underline"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="/kontakt"
              className="btn-primary no-underline"
              data-testid="nav-cta-button"
            >
              <Phone className="w-4 h-4" />
              Beratung anfragen
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden bg-transparent border-none cursor-pointer p-2 text-reinert-grey-dark"
            onClick={() => setMenuOpen(true)}
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-7 h-7" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu ${menuOpen ? "open" : ""}`} data-testid="mobile-menu">
        <div className="flex justify-end p-6">
          <button
            onClick={() => setMenuOpen(false)}
            className="bg-transparent border-none cursor-pointer text-white"
            data-testid="mobile-menu-close"
          >
            <X className="w-8 h-8" />
          </button>
        </div>
        <div className="flex flex-col items-start px-10 gap-6 mt-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="mobile-menu-link no-underline"
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="/kontakt"
            className="mt-6 btn-primary text-lg no-underline"
            data-testid="mobile-cta-button"
          >
            <Phone className="w-5 h-5" />
            Beratung anfragen
          </a>
        </div>
        {/* Decorative yellow brush stroke */}
        <div className="absolute bottom-10 left-10 right-10 opacity-20">
          <svg viewBox="0 0 300 8" preserveAspectRatio="none" style={{ width: "100%", height: "8px" }}>
            <path d="M0,4 C50,1 100,7 150,4 C200,1 250,7 300,4" stroke="#FFCC00" strokeWidth="3" fill="none" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════
   HERO SLIDER
   ═══════════════════════════════════════════════ */
function HeroSlider() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    const interval = setInterval(() => emblaApi.scrollNext(), 4000);
    return () => { clearInterval(interval); emblaApi.off("select", onSelect); };
  }, [emblaApi, onSelect]);

  const slides = [
    { type: "logo" },
    ...FEATURES.map(f => ({ type: "feature", ...f })),
  ];

  return (
    <div className="hero-image-container" data-testid="hero-slider">
      <div className="overflow-hidden rounded-2xl" ref={emblaRef}>
        <div className="flex" style={{ backfaceVisibility: 'hidden' }}>
          {slides.map((slide, i) => (
            <div className="flex-[0_0_100%] min-w-0" key={i}>
              {slide.type === "logo" ? (
                <div className="w-full h-[340px] md:h-[480px] lg:h-[540px] flex items-center justify-center" style={{ backgroundColor: '#FFFFFF' }}>
                  <img
                    src="/logo-reinert.webp"
                    alt="Malerbetrieb Reinert – Malerarbeiten und Bodenbeläge in Bad Neuenahr-Ahrweiler"
                    className="max-h-[55%] max-w-[75%] object-contain"
                    data-testid="slider-logo"
                  />
                </div>
              ) : (
                <a
                  href={`/service/${slide.slug}`}
                  className="block w-full h-[340px] md:h-[480px] lg:h-[540px] relative overflow-hidden no-underline"
                  data-testid={`slider-${slide.slug}`}
                >
                  {/* Brush stroke texture background */}
                  <div className="absolute inset-0" style={{ backgroundColor: '#0033A0' }} />
                  <img
                    src="/brush-slider-bg.webp"
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                    style={{ opacity: 0.7, mixBlendMode: 'overlay' }}
                  />
                  <div className="relative z-10 h-full flex flex-col items-center justify-center px-8 text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: 'rgba(255,204,0,0.15)', border: '2px solid rgba(255,204,0,0.5)' }}>
                      <slide.icon className="w-8 h-8 md:w-10 md:h-10" style={{ color: '#FFCC00' }} />
                    </div>
                    <h3 className="font-display font-bold text-2xl md:text-4xl mb-3" style={{ color: '#FFFFFF' }}>{slide.title}</h3>
                    <p className="font-body text-sm md:text-base max-w-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>{slide.description}</p>
                    <span className="inline-flex items-center gap-2 mt-5 font-body font-semibold text-sm" style={{ color: '#FFCC00' }}>
                      Mehr erfahren <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Dots */}
      <div className="flex justify-center gap-2 absolute bottom-4 left-0 right-0 z-10">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => emblaApi?.scrollTo(i)}
            className="border-none cursor-pointer rounded-full transition-all duration-300"
            style={{
              width: selectedIndex === i ? 24 : 8,
              height: 8,
              background: selectedIndex === i ? '#FFCC00' : 'rgba(255,255,255,0.4)',
            }}
            data-testid={`slider-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════
   HERO SECTION
   ═══════════════════════════════════════════════ */
function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: pos, behavior: "smooth" });
    }
  };

  return (
    <section className="hero-section pt-24 md:pt-28 pb-4 md:pb-6 px-6 lg:px-8" id="hero" data-testid="hero-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left: Text */}
        <div className="order-2 lg:order-1">
          <RevealOnScroll>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-reinert-yellow-light rounded-full text-reinert-blue-DEFAULT text-xs font-body font-semibold tracking-wide uppercase mb-6" data-testid="hero-tagline">
              <Palette className="w-3.5 h-3.5" />
              Seit 1999 &middot; Bad Neuenahr-Ahrweiler
            </span>
          </RevealOnScroll>

          <RevealOnScroll delay={0.1}>
            <h1 className="font-display font-extrabold text-5xl md:text-6xl xl:text-7xl text-reinert-grey-dark leading-[0.95] tracking-tight mb-2" data-testid="hero-headline">
              WIR VERWANDELN
              <br />
              <span className="text-reinert-blue-DEFAULT">RÄUME</span> IN
              <br />
              <span style={{ display: "inline-block" }}>
                LEBENSTRÄUME.
                <BrushStrokeUnderline />
              </span>
            </h1>
          </RevealOnScroll>

          <RevealOnScroll delay={0.2}>
            <p className="font-body text-reinert-grey-light text-base md:text-lg leading-relaxed mt-8 max-w-lg" data-testid="hero-description">
              Professionelle Malerarbeiten und Bodenbeläge – termingerecht, sauber
              und mit Leidenschaft für Perfektion. Ihr Anstreicher des Vertrauens
              im Umkreis von 50 km.
            </p>
          </RevealOnScroll>

          <RevealOnScroll delay={0.3}>
            <div className="flex flex-wrap gap-4 mt-10">
              <a href="/kontakt" className="btn-primary no-underline" data-testid="hero-cta-primary">
                Beratung anfragen
                <ArrowRight className="w-4 h-4" />
              </a>
              <button onClick={() => scrollTo("leistungen")} className="btn-secondary" data-testid="hero-cta-secondary">
                Unsere Leistungen
              </button>
            </div>
          </RevealOnScroll>
        </div>

        {/* Right: Slider */}
        <RevealOnScroll className="order-1 lg:order-2 relative" delay={0.15}>
          <div className="hero-accent-block" />
          <HeroSlider />
          <div className="hero-dot-pattern hidden lg:block" />
        </RevealOnScroll>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   STATS BAR
   ═══════════════════════════════════════════════ */
function StatsBar() {
  const { ref, isRevealed } = useScrollReveal(0.2);
  return (
    <section className="relative py-20 md:py-28 my-2 md:my-4 overflow-hidden" data-testid="stats-section" ref={ref}>
      <img
        src="/brush-stroke.webp"
        alt=""
        aria-hidden="true"
        draggable="false"
        className={`brush-bg ${isRevealed ? 'animate-in' : ''}`}
        style={{ top: '-20%', left: 0, width: '100%', height: '140%', objectFit: 'fill' }}
      />
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 relative z-10">
        <StatItem target={25} suffix="+" label="Jahre Erfahrung" />
        <StatItem target={100} suffix=" km+" label="Einsatzradius" />
        <StatItem target={1000} suffix="+" label="Projekte" />
        <StatItem target={101} suffix="%" label="Leidenschaft" />
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   SERVICES
   ═══════════════════════════════════════════════ */
function ServiceCard({ service, delay = 0 }) {
  const Icon = service.icon;
  return (
    <RevealOnScroll delay={delay}>
      <a href={`/service/${service.slug}`} className="no-underline block h-full">
        <div className="service-card group cursor-pointer h-full" data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className="relative overflow-hidden aspect-[4/3]">
            <img
              src={service.image}
              alt={`${service.title} – Malerbetrieb Reinert Bad Neuenahr`}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            <div className="absolute top-4 left-4 w-10 h-10 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center">
              <Icon className="w-5 h-5 text-reinert-blue-DEFAULT" />
            </div>
          </div>
          <div className="card-yellow-line" />
          <div className="p-6">
            <h3 className="font-display text-xl font-bold text-reinert-grey-dark mb-2">
              {service.title}
            </h3>
            <p className="text-reinert-grey-light text-sm font-body leading-relaxed mb-4">
              {service.description}
            </p>
            <span className="inline-flex items-center gap-1 text-reinert-blue-DEFAULT font-body font-semibold text-sm transition-all duration-300 group-hover:gap-3">
              Mehr erfahren <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </a>
    </RevealOnScroll>
  );
}

function ServicesSection() {
  return (
    <section className="pt-12 md:pt-16 pb-8 md:pb-12 px-6 lg:px-8 bg-reinert-blue-lighter" id="leistungen" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <RevealOnScroll>
          <span className="text-reinert-blue-DEFAULT font-body font-semibold text-sm tracking-[0.2em] uppercase">
            Was wir bieten
          </span>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl xl:text-6xl text-reinert-grey-dark mt-3 mb-4">
            UNSERE LEISTUNGEN
          </h2>
          <div className="w-20 h-1 bg-reinert-yellow-DEFAULT rounded-full mb-14" />
        </RevealOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.title} service={s} delay={i * 0.1} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   ABOUT SECTION
   ═══════════════════════════════════════════════ */
function AboutSection() {
  const checkPoints = [
    "Individuelle Beratung vor Ort",
    "Termintreue & Zuverlässigkeit",
    "Saubere Baustellen – garantiert",
    "Komplettservice auf Wunsch",
  ];

  return (
    <section className="pt-8 md:pt-12 pb-14 md:pb-20 px-6 lg:px-8 bg-white" id="ueber-uns" data-testid="about-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Image */}
        <RevealOnScroll>
          <div className="about-image-wrapper">
            <img
              src={IMAGES.about}
              alt="Professionelle Farbauswahl und Werkzeuge – Malerbetrieb Reinert"
              className="w-full h-[350px] md:h-[450px] object-cover rounded-2xl"
              loading="lazy"
              data-testid="about-image"
            />
          </div>
        </RevealOnScroll>

        {/* Text */}
        <div>
          <RevealOnScroll>
            <span className="text-reinert-blue-DEFAULT font-body font-semibold text-sm tracking-[0.2em] uppercase">
              Über Uns
            </span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl xl:text-5xl text-reinert-grey-dark mt-3 mb-6 leading-tight">
              IHR HANDWERKSBETRIEB
              <br />
              DES VERTRAUENS
            </h2>
          </RevealOnScroll>

          <RevealOnScroll delay={0.1}>
            <p className="font-body text-reinert-grey-light leading-relaxed mb-4">
              Seit der Gründung im Jahr 1999 steht Malerbetrieb Reinert für
              höchste Qualität und absolute Kundenzufriedenheit. Jeder Auftrag
              beginnt mit einem persönlichen Gespräch – am besten vor Ort.
            </p>
            <p className="font-body text-reinert-grey-light leading-relaxed mb-8">
              Wir sehen immer den Kunden und seine Wünsche als Basis der
              Zusammenarbeit. Im Umkreis von 50 km um Bad Neuenahr-Ahrweiler sind
              wir zuverlässig für Sie im Einsatz – und jede Baustelle verlassen
              wir sauber und aufgeräumt.
            </p>
          </RevealOnScroll>

          <RevealOnScroll delay={0.2}>
            <div className="space-y-3">
              {checkPoints.map((point) => (
                <div key={point} className="flex items-center gap-3" data-testid={`about-check-${point.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="w-6 h-6 rounded-full bg-reinert-yellow-DEFAULT flex items-center justify-center flex-shrink-0">
                    <svg className="w-3.5 h-3.5 text-reinert-blue-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-body font-medium text-reinert-grey-dark text-sm">
                    {point}
                  </span>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   WHY US SECTION
   ═══════════════════════════════════════════════ */
function WhyUsSection() {
  const { ref, isRevealed } = useScrollReveal(0.15);
  return (
    <section className="relative pt-10 md:pt-12 pb-10 md:pb-12 my-2 md:my-4 px-6 lg:px-8 overflow-hidden" id="vorteile" data-testid="why-us-section" ref={ref}>
      {/* Brush stroke background */}
      <img
        src="/brush-stroke-tall-fill.webp"
        alt=""
        aria-hidden="true"
        draggable="false"
        className={`brush-bg ${isRevealed ? 'animate-in' : ''}`}
        style={{ top: '-50%', left: 0, width: '100%', height: '200%', objectFit: 'fill' }}
      />
      {/* Content on top */}
      <div className="max-w-7xl mx-auto relative z-10">
        <RevealOnScroll>
          <span className="font-body font-semibold text-sm tracking-[0.2em] uppercase" style={{ color: '#FFCC00' }}>
            Ihre Vorteile
          </span>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl xl:text-6xl text-white mt-3 mb-4">
            WARUM REINERT?
          </h2>
          <div className="w-20 h-1 rounded-full mb-6" style={{ background: '#FFCC00' }} />
        </RevealOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <RevealOnScroll key={f.title} delay={i * 0.1}>
                <a href={`/service/${f.slug}`} className="no-underline block h-full">
                  <div className="feature-box h-full" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ background: 'rgba(255, 204, 0, 0.25)', border: '1px solid rgba(255, 204, 0, 0.35)' }}>
                      <Icon className="w-8 h-8" style={{ color: '#FFCC00' }} />
                    </div>
                    <h3 className="font-display text-xl font-bold text-white mb-3" style={{ textShadow: '0 1px 8px rgba(0,0,50,0.6)' }}>
                      {f.title}
                    </h3>
                    <p className="text-white font-body text-sm leading-relaxed" style={{ textShadow: '0 1px 8px rgba(0,0,50,0.6)' }}>
                      {f.description}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-4 font-body font-semibold text-sm transition-all duration-300" style={{ color: '#FFCC00' }}>
                      Mehr erfahren <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </a>
              </RevealOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   CTA SECTION
   ═══════════════════════════════════════════════ */
function CTASection() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: pos, behavior: "smooth" });
    }
  };

  return (
    <section className="cta-section bg-reinert-yellow-DEFAULT py-20 md:py-24 px-6 lg:px-8" data-testid="cta-section">
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <RevealOnScroll>
          <h2 className="font-display font-extrabold text-3xl md:text-5xl xl:text-6xl text-reinert-blue-dark leading-tight mb-6" data-testid="cta-headline">
            BEREIT FÜR IHR
            <br />
            NÄCHSTES PROJEKT?
          </h2>
        </RevealOnScroll>
        <RevealOnScroll delay={0.1}>
          <p className="font-body text-reinert-blue-dark/70 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            Lassen Sie uns gemeinsam Ihre Wohnträume verwirklichen. Kontaktieren
            Sie uns für eine unverbindliche Beratung – wir freuen uns auf Ihr
            Projekt!
          </p>
        </RevealOnScroll>
        <RevealOnScroll delay={0.2}>
          <a href="/kontakt" className="btn-yellow no-underline" data-testid="cta-button">
            Jetzt Kontakt aufnehmen
            <ArrowRight className="w-5 h-5" />
          </a>
        </RevealOnScroll>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   FOOTER
   ═══════════════════════════════════════════════ */
function Footer() {
  return (
    <footer className="bg-reinert-grey-dark pt-16 pb-8 px-6 lg:px-8" id="kontakt" data-testid="footer-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <LogoMark size={44} />
            </div>
            <p className="text-white/50 font-body text-sm leading-relaxed">
              Qualitätshandwerk seit 1999 in Bad Neuenahr-Ahrweiler. Ihr
              zuverlässiger Partner für Maler- und Bodenarbeiten.
            </p>
          </div>

          {/* Leistungen */}
          <div>
            <h4 className="font-display font-bold text-white text-base mb-5 tracking-wide">
              LEISTUNGEN
            </h4>
            <div className="flex flex-col gap-3">
              <a href="/service/malerhandwerk" className="footer-link">Malerhandwerk</a>
              <a href="/service/bodenhandwerk" className="footer-link">Bodenhandwerk</a>
              <a href="/service/spezial-angebote" className="footer-link">Spezial-Angebote</a>
              <a href="/service/referenzen" className="footer-link">Referenzen</a>
            </div>
          </div>

          {/* Unternehmen */}
          <div>
            <h4 className="font-display font-bold text-white text-base mb-5 tracking-wide">
              UNTERNEHMEN
            </h4>
            <div className="flex flex-col gap-3">
              <a href="/impressum" className="footer-link">Impressum</a>
              <a href="/datenschutz" className="footer-link">Datenschutz</a>
              <a href="/kontakt" className="footer-link">Kontakt</a>
            </div>
          </div>

          {/* Kontakt */}
          <div>
            <h4 className="font-display font-bold text-white text-base mb-5 tracking-wide">
              KONTAKT
            </h4>
            <div className="flex flex-col gap-4">
              <a href="tel:+492641205474" className="flex items-center gap-3 footer-link" data-testid="footer-phone">
                <Phone className="w-4 h-4 text-reinert-yellow-DEFAULT flex-shrink-0" />
                02641 / 205474
              </a>
              <a href="mailto:info@malerbetrieb-reinert.de" className="flex items-center gap-3 footer-link" data-testid="footer-email">
                <Mail className="w-4 h-4 text-reinert-yellow-DEFAULT flex-shrink-0" />
                info@malerbetrieb-reinert.de
              </a>
              <div className="flex items-start gap-3 footer-link" data-testid="footer-address">
                <MapPin className="w-4 h-4 text-reinert-yellow-DEFAULT flex-shrink-0 mt-0.5" />
                <span>
                  Walporzheimer Str. 100
                  <br />
                  53474 Bad Neuenahr-Ahrweiler
                </span>
              </div>
              <div className="flex items-start gap-3 footer-link" data-testid="footer-hours">
                <Clock className="w-4 h-4 text-reinert-yellow-DEFAULT flex-shrink-0 mt-0.5" />
                <span>Mo–Fr 07:00–16:00</span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/30 font-body text-xs" data-testid="footer-copyright">
            &copy; {new Date().getFullYear()} Malerbetrieb Reinert. Alle Rechte vorbehalten.
          </p>
          <div className="flex items-center gap-1 text-white/30 text-xs font-body">
            <span>Handwerk mit</span>
            <svg className="w-3.5 h-3.5 text-reinert-yellow-DEFAULT" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            <span>aus Bad Neuenahr</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════
   SCROLL TO TOP BUTTON
   ═══════════════════════════════════════════════ */
function ScrollToTopBtn() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      className={`scroll-top-btn ${visible ? "" : "hidden"}`}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      data-testid="scroll-to-top-button"
      aria-label="Nach oben scrollen"
    >
      <ChevronUp className="w-5 h-5" />
    </button>
  );
}

/* ═══════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════ */
function HomePage() {
  useEffect(() => {
    document.title = "Maler in Bad Neuenahr – Malerarbeiten & Bodenbeläge";
  }, []);

  return (
    <div data-testid="app-root">
      <PaintLoadAnimation />
      <Navigation />
      <main>
        <Hero />
        <StatsBar />
        <ServicesSection />
        <AboutSection />
        <WhyUsSection />
        <CTASection />
      </main>
      <Footer />
      <ScrollToTopBtn />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/service/:slug" element={<ServicePage />} />
        <Route path="/kontakt" element={<ContactPage />} />
        <Route path="/impressum" element={<LegalPage type="impressum" />} />
        <Route path="/datenschutz" element={<LegalPage type="datenschutz" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
